import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, DoorOpen, Download, MapPin, QrCode, Sun, Ticket } from "lucide-react";
import PrixBillet from "../components/PrixBillet";
import { Groupe, Ligne } from "./ui";

/* Mes billets (Jen, 31.10.2026 : « la famille doit aussi avoir ses billets sur
   l'application ») : les billets payés à l'adresse du compte, une carte par représentation.
   On la touche → le billet en plein écran, QR code en grand, prêt à scanner à l'entrée —
   même sans retrouver l'e-mail. Le soir du spectacle, l'Accueil les montre tout en haut. */
const API = process.env.REACT_APP_BACKEND_URL;
const ROSE = "#DB2777";
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const personnes = (n) => `${n} personne${n > 1 ? "s" : ""}`;

/* Le spectacle est aujourd'hui ou demain : les billets passent en tête de l'Accueil. */
export const billetsDuJour = (billets) => {
  const j = (d) => d.toISOString().slice(0, 10);
  const auj = new Date();
  const demain = new Date(auj.getTime() + 86400000);
  return (billets || []).some((b) => b.date === j(auj) || b.date === j(demain));
};

/* Dans « Mes cours » (Jen, 02.11.2026 : « toutes les réservations que fait une famille
   doivent être visibles dans leur app - sous mes cours ») : les billets de juin n'y sont pas
   recopiés — le QR code a un seul endroit, l'Accueil, où il monte tout en haut le soir du
   spectacle —, une ligne y mène et descend jusqu'à « Mes billets ». */
export const RappelBillets = ({ billets, onOnglet }) => {
  if (!billets?.length || !onOnglet) return null;
  const n = billets.length;
  const titres = [...new Set(billets.map((b) => b.titre))].join(" · ");
  const voir = () => {
    onOnglet("aujourdhui");
    // Après la remontée en haut de l'onglet (Espace.jsx), on descend jusqu'aux billets.
    setTimeout(() => document.querySelector("[data-testid=espace-mes-billets]")
      ?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };
  return (
    <Groupe testId="espace-rappel-billets">
      <Ligne Icone={Ticket} couleur={ROSE} onClick={voir} testId="espace-vers-billets"
        titre={n > 1 ? `Mes ${n} billets de spectacle` : "Mon billet de spectacle"}
        sousTitre={`${titres} · QR code dans l'Accueil`} />
    </Groupe>
  );
};

const Entres = ({ b }) => {
  if (!b.entres) return null;
  const tous = b.entres >= b.total;
  return (
    <div className="text-sm font-semibold rounded-full px-3 py-1 inline-block"
      style={tous ? { background: "#DCFCE7", color: "#166534" } : { background: "#FEF3C7", color: "#92400E" }}
      data-testid="billet-entres">
      {tous ? "Tout le monde est entré" : `${b.entres} sur ${b.total} déjà entrés`}
    </div>
  );
};

/* Le billet en plein écran. « ‹ Retour » reste en haut, sous l'encoche, et « Fermer le
   billet » attend en bas (Jen, 01.11.2026 : « quand je clique sur un billet, je n'ai pas de
   bouton de retour »). Le geste « retour » du téléphone le ferme aussi, sans quitter l'app.

   Posé dans un portail, directement sous <body>, au-dessus de tout (Jen, 02.11.2026 : « le
   menu retour disparaît caché sous la barre du haut et l'écran freeze complètement »). Rendu
   dans l'écran qui défile de l'app, il restait enfermé dans sa pile d'affichage sur iPhone
   (-webkit-overflow-scrolling) : l'en-tête recouvrait « ‹ Retour », la barre d'onglets
   recouvrait « Fermer le billet », plus rien ne répondait. Même cause que les feuilles de
   l'app école et de l'app prof. La barre du haut ne défile plus du tout (seul le contenu
   défile, en dessous) : « ‹ Retour » ne peut plus sortir de l'écran. L'écran de l'app ne
   défile plus derrière le billet, et redevient défilable à la fermeture quoi qu'il arrive :
   on efface notre réglage au lieu de remettre une valeur retenue. */
const BilletPleinEcran = ({ b, onFermer }) => {
  useEffect(() => {
    const touche = (e) => e.key === "Escape" && onFermer();
    window.addEventListener("keydown", touche);
    const fond = document.getElementById("espace-defilement");
    if (fond) fond.style.overflowY = "hidden";
    return () => {
      window.removeEventListener("keydown", touche);
      if (fond) fond.style.overflowY = "";
    };
  }, [onFermer]);
  return createPortal(
    <div className="fixed inset-0 z-[90] bg-white flex flex-col" role="dialog" aria-modal="true"
      aria-label={`Billet ${b.titre}`} data-testid="billet-plein-ecran" style={{ color: "var(--text)" }}>
      <div className="shrink-0 border-b"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)", background: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderColor: "var(--border)" }}>
        <div className="max-w-md mx-auto pl-2 pr-5 h-[52px] flex items-center justify-between gap-3">
          <button onClick={onFermer} className="inline-flex items-center text-[17px] font-medium min-h-[44px] pr-3"
            style={{ color: "var(--primary)", touchAction: "manipulation" }} data-testid="billet-fermer">
            <ChevronLeft size={26} strokeWidth={2.2} /> Retour
          </button>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] truncate" style={{ color: ROSE }}>Billet · {b.numero}</div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}
        data-testid="billet-contenu">
        <div className="max-w-md mx-auto px-5" style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}>
          <h2 className="font-display text-[28px] leading-tight mt-4">{b.titre}</h2>
          <div className="text-[15px] font-semibold mt-1">{majuscule(b.quand)}</div>
          <div className="mt-5 rounded-3xl border p-5 flex flex-col items-center" style={{ borderColor: "var(--border)" }}>
            <img src={`${API}/api/billetterie/billets/${b.jeton}/qr.svg`} alt={`QR code du billet ${b.numero}`}
              className="w-full max-w-[300px] aspect-square" data-testid="billet-qr-app" />
            <div className="text-[15px] font-semibold text-center mt-3">Présente ce QR code à l'entrée</div>
            <div className="text-[12.5px] text-[var(--muted)] text-center mt-1 flex items-center gap-1.5">
              <Sun size={13} /> Monte la luminosité de ton écran
            </div>
            <div className="mt-3"><Entres b={b} /></div>
            {/* Dans une nouvelle fenêtre (« OK » pour revenir) : dans l'app installée sur
                iPhone, le PDF remplaçait l'application, sans aucun moyen d'en revenir. */}
            <a href={`${API}/api/billetterie/billets/${b.jeton}/pdf`} download={`billet-${b.numero}.pdf`}
              target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold rounded-full px-4 py-2 border"
              style={{ color: ROSE, borderColor: "#FBCFE8" }} data-testid="billet-pdf-app"><Download size={15} /> Enregistrer le billet (PDF)</a>
          </div>
          {b.prix?.length ? <PrixBillet billet={b} className="mt-5" /> : (
            <ul className="mt-5 text-[15px] space-y-1" data-testid="billet-detail">
              {(b.detail || []).map((l) => <li key={l} className="font-medium">{l}</li>)}
            </ul>
          )}
          {(b.prenom || b.nom) && <div className="text-sm text-[var(--muted)] mt-2">Au nom de {b.prenom} {b.nom}{b.commande_le ? ` · commandé le ${b.commande_le}` : ""}</div>}
          <div className="mt-4 text-sm space-y-2">
            <div className="flex items-start gap-2"><DoorOpen size={16} className="mt-0.5 shrink-0" /> Ouverture des portes à {b.portes} · placement libre</div>
            <div className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0" />
              <span>{b.salle}<br /><span className="text-[var(--muted)]">{b.adresse}</span>
                {b.itineraire && <><br /><a href={b.itineraire} target="_blank" rel="noopener noreferrer" className="font-semibold" style={{ color: "var(--primary)" }}>Itinéraire</a></>}</span></div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-5">{b.conditions}</p>
          <button onClick={onFermer} className="mt-7 w-full rounded-full py-3.5 text-[15px] font-semibold"
            style={{ background: "#F1F5F9", color: "var(--text)", touchAction: "manipulation" }}
            data-testid="billet-fermer-bas">Fermer le billet</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const MesBillets = ({ billets }) => {
  const [ouvert, setOuvert] = useState(null);
  // Le billet ouvert a sa page dans l'historique : le geste ou le bouton « retour » du
  // téléphone le referme (avant, il quittait l'application). Même adresse, rien d'autre.
  // Chaque ouverture porte sa propre marque, et « Retour » ferme d'abord l'écran, sans
  // attendre l'historique (Jen, 02.11.2026) : une marque restée d'avant (app rouverte sur un
  // billet, page rechargée) faisait remonter « Retour » sur une entrée qui portait encore
  // « billet », et le billet restait ouvert — l'écran semblait figé.
  const marque = useRef("");
  useEffect(() => {
    const etat = window.history.state;
    if (etat?.billet) {
      const { billet: _ancienne, ...reste } = etat;
      try { window.history.replaceState(reste, ""); } catch { /* rien */ }
    }
  }, []);
  const ouvrir = (b) => {
    marque.current = `${b.numero}-${Date.now()}`;
    setOuvert(b);
    try { window.history.pushState({ ...(window.history.state || {}), billet: marque.current }, ""); } catch { /* rien */ }
  };
  const fermer = useCallback(() => {
    const notre = !!marque.current && window.history.state?.billet === marque.current;
    marque.current = "";
    setOuvert(null);
    if (notre) window.history.back();
  }, []);
  useEffect(() => {
    if (!ouvert) return undefined;
    const revenu = () => {
      if (window.history.state?.billet !== marque.current) { marque.current = ""; setOuvert(null); }
    };
    window.addEventListener("popstate", revenu);
    return () => window.removeEventListener("popstate", revenu);
  }, [ouvert]);
  if (!billets?.length) return null;
  return (
    <section data-testid="espace-mes-billets">
      <div className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)] px-1 mb-2">
        Mes billets
      </div>
      <div className="space-y-2">
        {billets.map((b) => (
          <button key={b.id} type="button" onClick={() => ouvrir(b)}
            className="w-full text-left rounded-3xl bg-white border flex items-center gap-3 p-3 transition-transform active:scale-[0.99]"
            style={{ borderColor: "var(--border)" }} data-testid={`espace-billet-${b.numero}`}>
            {b.affiche
              ? <img src={b.affiche} alt="" loading="lazy" className="w-14 h-20 rounded-xl object-cover shrink-0 bg-slate-100" />
              : <span className="w-14 h-20 rounded-xl shrink-0 bg-slate-100" />}
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: ROSE }}>{b.quand}</span>
              <span className="block font-display text-lg leading-tight mt-0.5">{b.titre}</span>
              <span className="block text-[12px] text-[var(--muted)] mt-0.5">{personnes(b.total)} · Billet {b.numero}</span>
            </span>
            <span className="shrink-0 flex flex-col items-center gap-1 text-[10.5px] font-semibold" style={{ color: "var(--primary)" }}>
              <span className="w-11 h-11 rounded-2xl grid place-items-center text-white" style={{ background: "var(--primary)" }}>
                <QrCode size={22} />
              </span>
              QR code
            </span>
          </button>
        ))}
      </div>
      {ouvert && <BilletPleinEcran b={ouvert} onFermer={fermer} />}
    </section>
  );
};
